// components/Admin/ProcedureManagement.jsx
// Procedure Code Catalog Management — ported from RIS procedureshow.php
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  Alert, Snackbar, CircularProgress, Switch, FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Save as SaveIcon, Close as CloseIcon, Search as SearchIcon
} from '@mui/icons-material';
import { procedureService } from '../../services/ris';

const MODALITIES = ['CT', 'MRI', 'XRAY', 'MAMMO', 'US', 'ANGIO', 'BMD', 'FLUORO', 'CR', 'DR', 'MR', 'MG', 'PT', 'NM', 'OT', 'DX'];

const emptyForm = {
  code: '', description: '', modalityType: '', bodyPart: '',
  chargeTotal: 0, portableCharge: 0, df: 0, timeUse: 0,
  biradFlag: false, prepId: '', cptCode: '', rvu: 0
};

export default function ProcedureManagement() {
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modalityFilter, setModalityFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchProcedures = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (modalityFilter) params.modality = modalityFilter;
      const { data } = await procedureService.list(params);
      setProcedures(data.procedures || []);
    } catch (err) {
      console.error('Failed to load procedures:', err);
    } finally {
      setLoading(false);
    }
  }, [search, modalityFilter]);

  useEffect(() => { fetchProcedures(); }, [fetchProcedures]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (proc) => { setEditing(proc); setForm({ ...proc }); setDialogOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await procedureService.update(editing._id, form);
        setSnackbar({ open: true, message: 'Procedure updated', severity: 'success' });
      } else {
        await procedureService.create(form);
        setSnackbar({ open: true, message: 'Procedure created', severity: 'success' });
      }
      setDialogOpen(false);
      fetchProcedures();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this procedure?')) return;
    try {
      await procedureService.delete(id);
      setSnackbar({ open: true, message: 'Procedure deactivated', severity: 'success' });
      fetchProcedures();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to deactivate', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>⚙️ Procedure Code Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Procedure</Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5}>
            <TextField fullWidth size="small" label="Search (code, description, body part)"
              value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ endAdornment: <SearchIcon color="action" /> }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" select label="Modality" value={modalityFilter}
              onChange={e => setModalityFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {MODALITIES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Chip label={`${procedures.length} procedures`} variant="outlined" />
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Modality</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Body Part</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Time (min)</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>CPT</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>RVU</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>BI-RADS</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : procedures.map(p => (
              <TableRow key={p._id} hover>
                <TableCell><Typography fontWeight={600} variant="body2">{p.code}</Typography></TableCell>
                <TableCell>{p.description}</TableCell>
                <TableCell><Chip label={p.modalityType} size="small" /></TableCell>
                <TableCell>{p.bodyPart}</TableCell>
                <TableCell>{p.timeUse || '-'}</TableCell>
                <TableCell>{p.cptCode || '-'}</TableCell>
                <TableCell>{p.rvu || '-'}</TableCell>
                <TableCell>{p.biradFlag ? <Chip label="Yes" size="small" color="info" /> : '-'}</TableCell>
                <TableCell>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(p)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Deactivate"><IconButton size="small" color="error" onClick={() => handleDelete(p._id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '640px', md: '680px' },
            maxWidth: '700px !important',
            maxHeight: '88vh',
            borderRadius: '14px',
          }
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, fontSize: '1.05rem', fontWeight: 700 }}>
          {editing ? 'Edit Procedure' : 'New Procedure'}
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 1.5 }}>
          <Grid container spacing={1.5} sx={{ mt: 0.2 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Code" value={form.code} size="small"
                onChange={e => setForm(f => ({ ...f, code: e.target.value }))} disabled={!!editing} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required select label="Modality" value={form.modalityType} size="small"
                onChange={e => setForm(f => ({ ...f, modalityType: e.target.value }))}>
                {MODALITIES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required label="Description" value={form.description} size="small"
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Body Part" value={form.bodyPart} size="small"
                onChange={e => setForm(f => ({ ...f, bodyPart: e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth label="Time (min)" type="number" value={form.timeUse} size="small"
                onChange={e => setForm(f => ({ ...f, timeUse: parseInt(e.target.value) || 0 }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel control={
                <Switch checked={form.biradFlag} onChange={e => setForm(f => ({ ...f, biradFlag: e.target.checked }))} size="small" />
              } label="BI-RADS" sx={{ mt: 0.5 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="CPT Code" value={form.cptCode} size="small"
                onChange={e => setForm(f => ({ ...f, cptCode: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="RVU" type="number" value={form.rvu} size="small"
                inputProps={{ step: 0.1, min: 0 }}
                onChange={e => setForm(f => ({ ...f, rvu: parseFloat(e.target.value) || 0 }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25 }}>
          <Button onClick={() => setDialogOpen(false)} size="small" sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} size="small"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', px: 2.5 }}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
